import {
  getObject,
  getBoolean,
  getLeoUuid,
  LeoUUID,
} from "@surya-digital/leo-ts-types";
import { SurveySettings, SurveySettingsDTO, SurveySettingsEnums } from "../types/surveySettings";
import { SurveyName, SurveyNameDTO } from "../types/surveyName";
import { SurveyTheme } from "../types/surveyTheme";
import { SurveySettingDialogProperties, SurveySettingDialogPropertiesDTO } from "../types/surveySettingDialogProperties";
import { SurveyLocalizedText, SurveyLocalizedTextDTO } from "../assets/surveyLocalizedText";
import { SurveyRespondentHeaderTextType } from "../types/surveyRespondentHeaderTextType";

export interface BackOfficeSurveySettingsDTO {
  surveySettings: SurveySettingsDTO;
  surveyName: SurveyNameDTO;
  selectedSurveyTheme: SurveyTheme.SurveyThemeDTO;
  isSubmitConfirmationRequired: boolean;
  submitConfirmationDialogProperties: SurveySettingDialogPropertiesDTO;
  promptUnansweredQuestions: boolean;
  promptUnansweredQuestionsDialogProperties: SurveySettingDialogPropertiesDTO;
  isRestartAllowed: boolean;
  restartSurveyDialogProperties: SurveySettingDialogPropertiesDTO;
  isSaveDraftAllowed: boolean;
  saveDraftDialogProperties: SurveySettingDialogPropertiesDTO;
  isInfoButtonShown: boolean;
  surveyInformationDialogProperties: SurveySettingDialogPropertiesDTO;
  isWelcomePageShown: boolean;
  welcomePageText: SurveyLocalizedTextDTO;
  thankYouPageText: SurveyLocalizedTextDTO;
  closedSurveyErrorTextTitle: SurveyLocalizedTextDTO;
  closedSurveyErrorTextDescription: SurveyLocalizedTextDTO;
  clientLightThemeLogoDocumentId: string;
  clientDarkThemeLogoDocumentId: string;
  surveyRespondentHeaderType: SurveyRespondentHeaderTextType.SurveyRespondentHeaderTextTypeDTO;
}

export class BackOfficeSurveySettings {
  readonly isBackOfficeSurveySettingsBackOfficeSurveySettings: boolean = true;

  readonly surveySettings: SurveySettings;
  readonly surveyName: SurveyName;
  readonly selectedSurveyTheme: SurveyTheme.SurveyTheme;
  readonly isSubmitConfirmationRequired: boolean;
  readonly submitConfirmationDialogProperties: SurveySettingDialogProperties;
  readonly promptUnansweredQuestions: boolean;
  readonly promptUnansweredQuestionsDialogProperties: SurveySettingDialogProperties;
  readonly isRestartAllowed: boolean;
  readonly restartSurveyDialogProperties: SurveySettingDialogProperties;
  readonly isSaveDraftAllowed: boolean;
  readonly saveDraftDialogProperties: SurveySettingDialogProperties;
  readonly isInfoButtonShown: boolean;
  readonly surveyInformationDialogProperties: SurveySettingDialogProperties;
  readonly isWelcomePageShown: boolean;
  readonly welcomePageText: SurveyLocalizedText;
  readonly thankYouPageText: SurveyLocalizedText;
  readonly closedSurveyErrorTextTitle: SurveyLocalizedText;
  readonly closedSurveyErrorTextDescription: SurveyLocalizedText;
  readonly clientLightThemeLogoDocumentId: LeoUUID;
  readonly clientDarkThemeLogoDocumentId: LeoUUID;
  readonly surveyRespondentHeaderType: SurveyRespondentHeaderTextType.SurveyRespondentHeaderTextType;


  constructor(
  surveySettings: SurveySettings, 
  surveyName: SurveyName, 
  selectedSurveyTheme: SurveyTheme.SurveyTheme, 
  isSubmitConfirmationRequired: boolean, 
  submitConfirmationDialogProperties: SurveySettingDialogProperties, 
  promptUnansweredQuestions: boolean, 
  promptUnansweredQuestionsDialogProperties: SurveySettingDialogProperties, 
  isRestartAllowed: boolean, 
  restartSurveyDialogProperties: SurveySettingDialogProperties, 
  isSaveDraftAllowed: boolean, 
  saveDraftDialogProperties: SurveySettingDialogProperties, 
  isInfoButtonShown: boolean, 
  surveyInformationDialogProperties: SurveySettingDialogProperties, 
  isWelcomePageShown: boolean, 
  welcomePageText: SurveyLocalizedText, 
  thankYouPageText: SurveyLocalizedText, 
  closedSurveyErrorTextTitle: SurveyLocalizedText, 
  closedSurveyErrorTextDescription: SurveyLocalizedText, 
  clientLightThemeLogoDocumentId: LeoUUID, 
  clientDarkThemeLogoDocumentId: LeoUUID, 
  surveyRespondentHeaderType: SurveyRespondentHeaderTextType.SurveyRespondentHeaderTextType
  ) {
    this.surveySettings = surveySettings;
    this.surveyName = surveyName;
    this.selectedSurveyTheme = selectedSurveyTheme;
    this.isSubmitConfirmationRequired = isSubmitConfirmationRequired;
    this.submitConfirmationDialogProperties = submitConfirmationDialogProperties;
    this.promptUnansweredQuestions = promptUnansweredQuestions;
    this.promptUnansweredQuestionsDialogProperties = promptUnansweredQuestionsDialogProperties;
    this.isRestartAllowed = isRestartAllowed;
    this.restartSurveyDialogProperties = restartSurveyDialogProperties;
    this.isSaveDraftAllowed = isSaveDraftAllowed;
    this.saveDraftDialogProperties = saveDraftDialogProperties;
    this.isInfoButtonShown = isInfoButtonShown;
    this.surveyInformationDialogProperties = surveyInformationDialogProperties;
    this.isWelcomePageShown = isWelcomePageShown;
    this.welcomePageText = welcomePageText;
    this.thankYouPageText = thankYouPageText;
    this.closedSurveyErrorTextTitle = closedSurveyErrorTextTitle;
    this.closedSurveyErrorTextDescription = closedSurveyErrorTextDescription;
    this.clientLightThemeLogoDocumentId = clientLightThemeLogoDocumentId;
    this.clientDarkThemeLogoDocumentId = clientDarkThemeLogoDocumentId;
    this.surveyRespondentHeaderType = surveyRespondentHeaderType;
  }

  static fromDTO(dto: BackOfficeSurveySettingsDTO): BackOfficeSurveySettings {
    const surveySettings = SurveySettings.fromDTO(getObject(dto, "surveySettings", InvalidBackOfficeSurveySettingsSurveySettingsError));
    const surveyName = SurveyName.fromDTO(getObject(dto, "surveyName", InvalidBackOfficeSurveySettingsSurveyNameError));
    const selectedSurveyTheme = SurveyTheme.fromDTO(getObject(dto, "selectedSurveyTheme", InvalidBackOfficeSurveySettingsSelectedSurveyThemeError));
    const isSubmitConfirmationRequired = getBoolean(dto, "isSubmitConfirmationRequired", InvalidBackOfficeSurveySettingsIsSubmitConfirmationRequiredError);
    const submitConfirmationDialogProperties = SurveySettingDialogProperties.fromDTO(getObject(dto, "submitConfirmationDialogProperties", InvalidBackOfficeSurveySettingsSubmitConfirmationDialogPropertiesError));
    const promptUnansweredQuestions = getBoolean(dto, "promptUnansweredQuestions", InvalidBackOfficeSurveySettingsPromptUnansweredQuestionsError);
    const promptUnansweredQuestionsDialogProperties = SurveySettingDialogProperties.fromDTO(getObject(dto, "promptUnansweredQuestionsDialogProperties", InvalidBackOfficeSurveySettingsPromptUnansweredQuestionsDialogPropertiesError));
    const isRestartAllowed = getBoolean(dto, "isRestartAllowed", InvalidBackOfficeSurveySettingsIsRestartAllowedError);
    const restartSurveyDialogProperties = SurveySettingDialogProperties.fromDTO(getObject(dto, "restartSurveyDialogProperties", InvalidBackOfficeSurveySettingsRestartSurveyDialogPropertiesError));
    const isSaveDraftAllowed = getBoolean(dto, "isSaveDraftAllowed", InvalidBackOfficeSurveySettingsIsSaveDraftAllowedError);
    const saveDraftDialogProperties = SurveySettingDialogProperties.fromDTO(getObject(dto, "saveDraftDialogProperties", InvalidBackOfficeSurveySettingsSaveDraftDialogPropertiesError));
    const isInfoButtonShown = getBoolean(dto, "isInfoButtonShown", InvalidBackOfficeSurveySettingsIsInfoButtonShownError);
    const surveyInformationDialogProperties = SurveySettingDialogProperties.fromDTO(getObject(dto, "surveyInformationDialogProperties", InvalidBackOfficeSurveySettingsSurveyInformationDialogPropertiesError));
    const isWelcomePageShown = getBoolean(dto, "isWelcomePageShown", InvalidBackOfficeSurveySettingsIsWelcomePageShownError);
    const welcomePageText = SurveyLocalizedText.fromDTO(getObject(dto, "welcomePageText", InvalidBackOfficeSurveySettingsWelcomePageTextError));
    const thankYouPageText = SurveyLocalizedText.fromDTO(getObject(dto, "thankYouPageText", InvalidBackOfficeSurveySettingsThankYouPageTextError));
    const closedSurveyErrorTextTitle = SurveyLocalizedText.fromDTO(getObject(dto, "closedSurveyErrorTextTitle", InvalidBackOfficeSurveySettingsClosedSurveyErrorTextTitleError));
    const closedSurveyErrorTextDescription = SurveyLocalizedText.fromDTO(getObject(dto, "closedSurveyErrorTextDescription", InvalidBackOfficeSurveySettingsClosedSurveyErrorTextDescriptionError));
    const clientLightThemeLogoDocumentId = getLeoUuid(dto, "clientLightThemeLogoDocumentId", InvalidBackOfficeSurveySettingsClientLightThemeLogoDocumentIdError);
    const clientDarkThemeLogoDocumentId = getLeoUuid(dto, "clientDarkThemeLogoDocumentId", InvalidBackOfficeSurveySettingsClientDarkThemeLogoDocumentIdError);
    const surveyRespondentHeaderType = SurveyRespondentHeaderTextType.fromDTO(getObject(dto, "surveyRespondentHeaderType", InvalidBackOfficeSurveySettingsSurveyRespondentHeaderTypeError));
    return new BackOfficeSurveySettings(
      surveySettings,
      surveyName,
      selectedSurveyTheme,
      isSubmitConfirmationRequired,
      submitConfirmationDialogProperties,
      promptUnansweredQuestions,
      promptUnansweredQuestionsDialogProperties,
      isRestartAllowed,
      restartSurveyDialogProperties,
      isSaveDraftAllowed,
      saveDraftDialogProperties,
      isInfoButtonShown,
      surveyInformationDialogProperties,
      isWelcomePageShown,
      welcomePageText,
      thankYouPageText,
      closedSurveyErrorTextTitle,
      closedSurveyErrorTextDescription,
      clientLightThemeLogoDocumentId,
      clientDarkThemeLogoDocumentId,
      surveyRespondentHeaderType
    );
  }

  toDTO(): BackOfficeSurveySettingsDTO {
    const dto: BackOfficeSurveySettingsDTO = {
        surveySettings: this.surveySettings.toDTO(),
        surveyName: this.surveyName.toDTO(),
        selectedSurveyTheme: SurveyTheme.toDTO(this.selectedSurveyTheme),
        isSubmitConfirmationRequired: this.isSubmitConfirmationRequired,
        submitConfirmationDialogProperties: this.submitConfirmationDialogProperties.toDTO(),
        promptUnansweredQuestions: this.promptUnansweredQuestions,
        promptUnansweredQuestionsDialogProperties: this.promptUnansweredQuestionsDialogProperties.toDTO(),
        isRestartAllowed: this.isRestartAllowed,
        restartSurveyDialogProperties: this.restartSurveyDialogProperties.toDTO(),
        isSaveDraftAllowed: this.isSaveDraftAllowed,
        saveDraftDialogProperties: this.saveDraftDialogProperties.toDTO(),
        isInfoButtonShown: this.isInfoButtonShown,
        surveyInformationDialogProperties: this.surveyInformationDialogProperties.toDTO(),
        isWelcomePageShown: this.isWelcomePageShown,
        welcomePageText: this.welcomePageText.toDTO(),
        thankYouPageText: this.thankYouPageText.toDTO(),
        closedSurveyErrorTextTitle: this.closedSurveyErrorTextTitle.toDTO(),
        closedSurveyErrorTextDescription: this.closedSurveyErrorTextDescription.toDTO(),
        clientLightThemeLogoDocumentId: this.clientLightThemeLogoDocumentId.toDTO(),
        clientDarkThemeLogoDocumentId: this.clientDarkThemeLogoDocumentId.toDTO(),
        surveyRespondentHeaderType: this.surveyRespondentHeaderType.toDTO()
    };
    return dto;
  }
  }

BackOfficeSurveySettings.prototype.toString = function toString(): string {
  return `BackOfficeSurveySettings(surveySettings='${this.surveySettings}',surveyName='${this.surveyName}',selectedSurveyTheme='${this.selectedSurveyTheme}',isSubmitConfirmationRequired='${this.isSubmitConfirmationRequired}',submitConfirmationDialogProperties='${this.submitConfirmationDialogProperties}',promptUnansweredQuestions='${this.promptUnansweredQuestions}',promptUnansweredQuestionsDialogProperties='${this.promptUnansweredQuestionsDialogProperties}',isRestartAllowed='${this.isRestartAllowed}',restartSurveyDialogProperties='${this.restartSurveyDialogProperties}',isSaveDraftAllowed='${this.isSaveDraftAllowed}',saveDraftDialogProperties='${this.saveDraftDialogProperties}',isInfoButtonShown='${this.isInfoButtonShown}',surveyInformationDialogProperties='${this.surveyInformationDialogProperties}',isWelcomePageShown='${this.isWelcomePageShown}',welcomePageText='${this.welcomePageText}',thankYouPageText='${this.thankYouPageText}',closedSurveyErrorTextTitle='${this.closedSurveyErrorTextTitle}',closedSurveyErrorTextDescription='${this.closedSurveyErrorTextDescription}',clientLightThemeLogoDocumentId='${this.clientLightThemeLogoDocumentId}',clientDarkThemeLogoDocumentId='${this.clientDarkThemeLogoDocumentId}',surveyRespondentHeaderType='${this.surveyRespondentHeaderType}')`;
};

export class InvalidBackOfficeSurveySettingsError extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidBackOfficeSurveySettingsError";
  }
}
export class InvalidBackOfficeSurveySettingsSurveySettingsError extends InvalidBackOfficeSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidBackOfficeSurveySettingsSurveySettingsError";
  }
}

export class InvalidBackOfficeSurveySettingsSurveyNameError extends InvalidBackOfficeSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidBackOfficeSurveySettingsSurveyNameError";
  }
}

export class InvalidBackOfficeSurveySettingsSelectedSurveyThemeError extends InvalidBackOfficeSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidBackOfficeSurveySettingsSelectedSurveyThemeError";
  }
}

export class InvalidBackOfficeSurveySettingsIsSubmitConfirmationRequiredError extends InvalidBackOfficeSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidBackOfficeSurveySettingsIsSubmitConfirmationRequiredError";
  }
}

export class InvalidBackOfficeSurveySettingsSubmitConfirmationDialogPropertiesError extends InvalidBackOfficeSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidBackOfficeSurveySettingsSubmitConfirmationDialogPropertiesError";
  }
}

export class InvalidBackOfficeSurveySettingsPromptUnansweredQuestionsError extends InvalidBackOfficeSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidBackOfficeSurveySettingsPromptUnansweredQuestionsError";
  }
}

export class InvalidBackOfficeSurveySettingsPromptUnansweredQuestionsDialogPropertiesError extends InvalidBackOfficeSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidBackOfficeSurveySettingsPromptUnansweredQuestionsDialogPropertiesError";
  }
}

export class InvalidBackOfficeSurveySettingsIsRestartAllowedError extends InvalidBackOfficeSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidBackOfficeSurveySettingsIsRestartAllowedError";
  }
}

export class InvalidBackOfficeSurveySettingsRestartSurveyDialogPropertiesError extends InvalidBackOfficeSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidBackOfficeSurveySettingsRestartSurveyDialogPropertiesError";
  }
}

export class InvalidBackOfficeSurveySettingsIsSaveDraftAllowedError extends InvalidBackOfficeSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidBackOfficeSurveySettingsIsSaveDraftAllowedError";
  }
}

export class InvalidBackOfficeSurveySettingsSaveDraftDialogPropertiesError extends InvalidBackOfficeSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidBackOfficeSurveySettingsSaveDraftDialogPropertiesError";
  }
}

export class InvalidBackOfficeSurveySettingsIsInfoButtonShownError extends InvalidBackOfficeSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidBackOfficeSurveySettingsIsInfoButtonShownError";
  }
}

export class InvalidBackOfficeSurveySettingsSurveyInformationDialogPropertiesError extends InvalidBackOfficeSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidBackOfficeSurveySettingsSurveyInformationDialogPropertiesError";
  }
}

export class InvalidBackOfficeSurveySettingsIsWelcomePageShownError extends InvalidBackOfficeSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidBackOfficeSurveySettingsIsWelcomePageShownError";
  }
}

export class InvalidBackOfficeSurveySettingsWelcomePageTextError extends InvalidBackOfficeSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidBackOfficeSurveySettingsWelcomePageTextError";
  }
}

export class InvalidBackOfficeSurveySettingsThankYouPageTextError extends InvalidBackOfficeSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidBackOfficeSurveySettingsThankYouPageTextError";
  }
}

export class InvalidBackOfficeSurveySettingsClosedSurveyErrorTextTitleError extends InvalidBackOfficeSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidBackOfficeSurveySettingsClosedSurveyErrorTextTitleError";
  }
}

export class InvalidBackOfficeSurveySettingsClosedSurveyErrorTextDescriptionError extends InvalidBackOfficeSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidBackOfficeSurveySettingsClosedSurveyErrorTextDescriptionError";
  }
}

export class InvalidBackOfficeSurveySettingsClientLightThemeLogoDocumentIdError extends InvalidBackOfficeSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidBackOfficeSurveySettingsClientLightThemeLogoDocumentIdError";
  }
}

export class InvalidBackOfficeSurveySettingsClientDarkThemeLogoDocumentIdError extends InvalidBackOfficeSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidBackOfficeSurveySettingsClientDarkThemeLogoDocumentIdError";
  }
}

export class InvalidBackOfficeSurveySettingsSurveyRespondentHeaderTypeError extends InvalidBackOfficeSurveySettingsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidBackOfficeSurveySettingsSurveyRespondentHeaderTypeError";
  }
}
